import { StyleSheet, Text, View } from 'react-native'
import { formatGBP } from '../format'
import { colors, fontFamily, fontSize, radius, space } from '../tokens'

type Comparable = {
  address: string
  band: string
  lastSalePrice: number | null
  lastSaleDate: string | null
}

type Props = {
  comparable: Comparable
  // Total comparables found for this band check (including the teaser). The
  // "+ N more in your pack" line surfaces (total - 1); render only the
  // teaser when total <= 1.
  totalCount: number
}

// Renders ONE named comparable as proof-of-case in the free verdict view.
// Every other comparable + every reasoning body lives in the £4.99 pack; the
// teaser plus "+ N more" line is the proof-without-paywall hook.
export const TeaserComparable = ({ comparable, totalCount }: Props) => {
  const remaining = Math.max(0, totalCount - 1)
  const saleLine =
    comparable.lastSalePrice && comparable.lastSaleDate
      ? `Band ${comparable.band} · sold ${formatGBP(comparable.lastSalePrice)} on ${comparable.lastSaleDate}`
      : `Band ${comparable.band}`
  return (
    <View style={styles.box}>
      <Text style={styles.label}>One example comparable</Text>
      <Text style={styles.address}>{comparable.address}</Text>
      <Text style={styles.detail}>{saleLine}</Text>
      {remaining > 0 ? (
        <Text style={styles.lock}>
          🔒 {remaining} more comparable{remaining === 1 ? '' : 's'} in your pack
        </Text>
      ) : null}
    </View>
  )
}

const styles = StyleSheet.create({
  box: {
    padding: space[16],
    borderRadius: radius.sm,
    backgroundColor: colors.primary[100],
    borderWidth: 1,
    borderColor: colors.primary[500],
    marginVertical: space[8],
    gap: space[4],
  },
  label: {
    fontSize: fontSize.tiny,
    color: colors.primary[900],
    fontWeight: '600',
    fontFamily: fontFamily.body,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  address: {
    fontSize: fontSize.small,
    color: colors.gray[900],
    fontWeight: '700',
    fontFamily: fontFamily.body,
  },
  detail: {
    fontSize: fontSize.small,
    color: colors.gray[700],
    fontFamily: fontFamily.body,
  },
  lock: {
    fontSize: fontSize.tiny,
    color: colors.primary[700],
    fontFamily: fontFamily.body,
    marginTop: space[4],
  },
})
